import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatDate, formatEUR, PROSPECT_STATUSES, ROLE_LABELS } from "@/lib/api";
import { PageHeader, PrimaryButton, TextField, SelectField, TextArea, EmptyState } from "@/components/ui-kit";
import StatusBadge from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { Search, Plus, UserPlus, X } from "lucide-react";
import { toast, Toaster } from "sonner";

export default function Prospects() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [statutFilter, setStatutFilter] = useState(searchParams.get("statut") || "");
  const [vendeurId, setVendeurId] = useState(searchParams.get("vendeur_id") || "");
  const [showForm, setShowForm] = useState(false);

  const canEdit = ["admin", "dirigeant", "manager_commercial", "vendeur"].includes(user?.role);

  const load = () => {
    setLoading(true);
    const params = {};
    if (statutFilter) params.statut = statutFilter;
    if (vendeurId) params.vendeur_id = vendeurId;
    if (search) params.search = search;
    api.get("/prospects", { params }).then((r) => setItems(r.data)).finally(() => setLoading(false));
  };

  // Sync state when arriving with new URL query params (e.g. from a dashboard link)
  useEffect(() => {
    setStatutFilter(searchParams.get("statut") || "");
    setVendeurId(searchParams.get("vendeur_id") || "");
    // eslint-disable-next-line
  }, [searchParams]);

  useEffect(() => {
    load();
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    // eslint-disable-next-line
  }, [statutFilter, vendeurId]);

  const handleSearch = (e) => {
    e.preventDefault();
    load();
  };

  const changeStatut = (val) => {
    setStatutFilter(val);
    const next = new URLSearchParams(searchParams);
    if (val) next.set("statut", val); else next.delete("statut");
    setSearchParams(next, { replace: true });
  };

  const clearVendeur = () => {
    setVendeurId("");
    const next = new URLSearchParams(searchParams);
    next.delete("vendeur_id");
    setSearchParams(next, { replace: true });
  };

  const vendeurName = (id) => users.find((u) => u.id === id)?.name || "—";

  return (
    <div className="p-8" data-testid="prospects-page">
      <Toaster position="top-right" richColors />
      <PageHeader
        title="Prospects"
        subtitle="Espace Commerce"
        actions={canEdit && (
          <PrimaryButton onClick={() => setShowForm(true)} data-testid="new-prospect-button">
            <span className="flex items-center gap-2"><Plus className="w-4 h-4" /> Nouveau prospect</span>
          </PrimaryButton>
        )}
      />

      <div className="bg-white rounded-sm border border-slate-200 mb-6 p-4 flex flex-wrap gap-3 items-end">
        <form onSubmit={handleSearch} className="flex-1 min-w-[200px] flex gap-2">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher (nom, prénom, email)"
              data-testid="prospects-search-input"
              className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-1 focus:ring-[#111111] text-sm"
            />
          </div>
        </form>
        <div className="min-w-[200px]">
          <SelectField
            testid="prospects-status-filter"
            value={statutFilter}
            onChange={(e) => changeStatut(e.target.value)}
            options={[{ value: "", label: "Tous les statuts" }, ...PROSPECT_STATUSES]}
          />
        </div>
        {vendeurId && (
          <button
            onClick={clearVendeur}
            data-testid="clear-vendeur-filter"
            className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-[#B8873A] text-[#8a6528] rounded-sm text-sm font-medium hover:bg-amber-100 transition-colors"
          >
            Vendeur : {vendeurName(vendeurId)} <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      <div className="bg-white rounded-sm border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-500">Chargement…</div>
        ) : items.length === 0 ? (
          <EmptyState icon={UserPlus} title="Aucun prospect" description="Cliquez sur 'Nouveau prospect' pour commencer." testid="prospects-empty" />
        ) : (
          <table className="w-full text-sm" data-testid="prospects-table">
            <thead className="bg-slate-50 text-slate-700">
              <tr>
                <th className="text-left px-4 py-3 font-semibold uppercase text-xs tracking-wider">Client</th>
                <th className="text-left px-4 py-3 font-semibold uppercase text-xs tracking-wider">Programme / Lot</th>
                <th className="text-left px-4 py-3 font-semibold uppercase text-xs tracking-wider">Vendeur</th>
                <th className="text-left px-4 py-3 font-semibold uppercase text-xs tracking-wider">Statut</th>
                <th className="text-right px-4 py-3 font-semibold uppercase text-xs tracking-wider">Prix</th>
                <th className="text-left px-4 py-3 font-semibold uppercase text-xs tracking-wider">
                  {statutFilter === "a_relancer" ? "Relance prévue" : statutFilter === "rdv_planifie" ? "RDV prévu" : "Créé le"}
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3">
                    <Link to={`/prospects/${p.id}`} className="font-semibold text-[#111111] hover:text-[#B8873A]" data-testid={`prospect-link-${p.id}`}>
                      {p.prenom} {p.nom}
                    </Link>
                    <div className="text-xs text-slate-500">{p.email || p.telephone || "—"}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm">{p.programme_recherche || "—"}</div>
                    <div className="text-xs text-slate-500">Lot {p.lot_propose || "—"}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{vendeurName(p.vendeur_id)}</td>
                  <td className="px-4 py-3"><StatusBadge value={p.statut} kind="prospect" /></td>
                  <td className="px-4 py-3 text-right font-medium">{formatEUR(p.prix)}</td>
                  <td className="px-4 py-3 text-slate-500" data-testid={`prospect-date-${p.id}`}>
                    {statutFilter === "a_relancer"
                      ? formatDate(p.date_prochaine_relance || p.relance_prochaine_date)
                      : statutFilter === "rdv_planifie"
                      ? formatDate(p.date_prochain_rdv)
                      : formatDate(p.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showForm && (
        <ProspectForm
          users={users}
          onClose={() => setShowForm(false)}
          onCreated={() => { setShowForm(false); load(); toast.success("Prospect créé"); }}
        />
      )}
    </div>
  );
}

function ProspectForm({ users, onClose, onCreated }) {
  const [form, setForm] = useState({
    prenom: "", nom: "", email: "", telephone: "",
    programme_recherche: "", lot_propose: "", promoteur: "", prix: "",
    vendeur_id: "", statut: "nouveau", niveau_rdv: "", source: "", notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [promoteurs, setPromoteurs] = useState([]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get("/promoteurs").then((r) => setPromoteurs(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.prix) payload.prix = parseFloat(payload.prix);
      else delete payload.prix;
      if (!payload.vendeur_id) delete payload.vendeur_id;
      await api.post("/prospects", payload);
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const vendeurs = users.filter((u) => ["vendeur", "manager_commercial", "admin", "dirigeant"].includes(u.role));

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-sm border border-slate-200 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="prospect-form-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 sticky top-0 bg-white">
          <h2 className="text-lg font-bold text-[#111111]" style={{fontFamily:"Cabinet Grotesk"}}>Nouveau prospect</h2>
          <button onClick={onClose} data-testid="close-form-button" className="text-slate-500 hover:text-slate-800"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextField label="Prénom *" value={form.prenom} onChange={(e) => set("prenom", e.target.value)} required testid="form-prenom" />
          <TextField label="Nom *" value={form.nom} onChange={(e) => set("nom", e.target.value)} required testid="form-nom" />
          <TextField label="Email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} testid="form-email" />
          <TextField label="Téléphone" value={form.telephone} onChange={(e) => set("telephone", e.target.value)} testid="form-telephone" />
          <TextField label="Programme recherché" value={form.programme_recherche} onChange={(e) => set("programme_recherche", e.target.value)} testid="form-programme" />
          <TextField label="Lot proposé" value={form.lot_propose} onChange={(e) => set("lot_propose", e.target.value)} testid="form-lot" />
          <TextField label="Promoteur" value={form.promoteur} onChange={(e) => set("promoteur", e.target.value)} testid="form-promoteur" list="promoteurs-suggestions" placeholder="Saisir ou choisir un promoteur connu" />
          <datalist id="promoteurs-suggestions">
            {promoteurs.map((p) => <option key={p.id} value={p.nom} />)}
          </datalist>
          <TextField label="Prix (€)" type="number" value={form.prix} onChange={(e) => set("prix", e.target.value)} testid="form-prix" />
          <SelectField label="Vendeur" testid="form-vendeur" value={form.vendeur_id} onChange={(e) => set("vendeur_id", e.target.value)}
            options={[{ value: "", label: "Non assigné" }, ...vendeurs.map((v) => ({ value: v.id, label: `${v.name} (${ROLE_LABELS[v.role] || v.role})` }))]} />
          <div className="grid grid-cols-2 gap-3">
            <SelectField label="Statut" testid="form-statut" value={form.statut} onChange={(e) => set("statut", e.target.value)} options={PROSPECT_STATUSES} />
            <SelectField label="Niveau RDV" testid="form-niveau-rdv" value={form.niveau_rdv} onChange={(e) => set("niveau_rdv", e.target.value)} options={[{value:"",label:"—"},{value:"R0",label:"R0 (contact tél.)"},{value:"R1",label:"R1"},{value:"R2",label:"R2"},{value:"R3",label:"R3"}]} />
          </div>
          <TextField label="Source" value={form.source} onChange={(e) => set("source", e.target.value)} placeholder="Ex: site web, salon, recommandation" testid="form-source" />
          <div className="md:col-span-2">
            <TextArea label="Notes internes" rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} testid="form-notes" />
          </div>
          <div className="md:col-span-2 flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-slate-300 rounded-sm text-sm font-semibold hover:bg-slate-50">Annuler</button>
            <PrimaryButton type="submit" disabled={saving} data-testid="submit-prospect-button">{saving ? "Enregistrement…" : "Créer le prospect"}</PrimaryButton>
          </div>
        </form>
      </div>
    </div>
  );
}
